import React, { useState, useEffect } from 'react';
import axios from 'axios';
import HeadingCp from '../components/HeadingCp';
import { formatTimeTo12HourUtil } from '../utils/formatTimeTo12HourUtil';
import LegendCP from '../components/LegendCP';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 6am to 8pm

const FullViewerMobile = () => {
  const [events, setEvents] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const apiUrl = process.env.REACT_APP_API_URL;
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState('All');

  useEffect(() => {
    fetchTechnicians();
    fetchEventsForDay(currentDate);
    // eslint-disable-next-line
  }, [currentDate]);

  const fetchTechnicians = async () => {
    try {
      const res = await axios.get(`${apiUrl}/contracts_crud.php?employee_list=true`);
      setTechnicians(res.data || []);
      // Extract unique departments
      const uniqueDepts = Array.from(new Set((res.data || []).map(t => t.department).filter(Boolean)));
      setDepartments(uniqueDepts);
    } catch (err) {
      // fail silently
    }
  };

  // Date parsing utility (for YYYY-MM-DD strings, always as LOCAL)
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0);
  };

  // Updated Normalizer: returns date_from and date_to (per your latest helper)
  const normalize = (data, type) => {
    return (Array.isArray(data) ? data : []).map(ev => ({
      type,
      id: ev.id || ev.schedule_id || ev.inspection_id,
      tech_id: ev.tech_id || ev.lead_tech_id || ev.TECH,
      add_tech: ev.add_tech || [],
      job_site: ev.job_site || ev.job_site_name || ev.ADDRESS,
      customer: ev.customer_name || ev.CUSTOMER,
      job_number: ev.job_number || '',
      date_from: ev.scheduled_due_date || ev.SCHEDULE_DATE || ev.schedule_from,
      date_to: ev.SCHEDULE_DATE || ev.schedule_date_to || ev.schedule_to,
      start_time: ev.schedule_time_in || ev.TIME || ev.start_time,
      end_time: ev.schedule_time_out || ev.end_time || ev.end_time,
      TECH: ev.TECH, // for mobile helper
      STATUS: ev.STATUS,
      TROUBLE: ev.TROUBLE || '',
    }));
  };

  // MAIN FIX: Filter merged events so only those that include currentDate (between date_from and date_to, inclusive)
  const fetchEventsForDay = async (date) => {
    const iso = date.toISOString().split('T')[0];
    try {
      const [insRes, svcRes, wipRes] = await Promise.all([
        axios.get(`${apiUrl}/contracts_crud.php`, { params: { getInspections: 'all', startDate: iso, endDate: iso } }),
        axios.get(`${apiUrl}/service_crud.php?getListByDate=1&date=${iso}`),
        axios.get(`${apiUrl}/wip_crud.php`, { params: { getWipScheduleList: 'all', startDate: iso, endDate: iso } }),
      ]);
      const merged = [
        ...normalize(insRes.data[0], 'inspection'),
        ...normalize(svcRes.data, 'service'),
        ...normalize(wipRes.data[0], 'wip'),
      ];
      // Only show events where currentDate falls within [date_from, date_to]
      const cd = new Date(date);
      cd.setHours(0, 0, 0, 0);
      const onlyEventsOnDate = merged.filter(ev => {
        const from = parseLocalDate(ev.date_from);
        const to = parseLocalDate(ev.date_to || ev.date_from); // support events with no end
        if (!from || !to) return false;
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return cd >= from && cd <= to;
      });
      setEvents(onlyEventsOnDate);
    } catch (err) {}
  };

  const filteredTechs =
    selectedDept === 'All'
      ? technicians
      : technicians.filter(tech => tech.department === selectedDept);

  const handlePreviousDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    setCurrentDate(prev);
  };

  const handleNextDay = () => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + 1);
    setCurrentDate(next);
  };

  return (
    <div className="w-full max-w-md mx-auto p-2">
      <HeadingCp label="Full Scheduler (Mobile)" />
      <div className="flex flex-wrap gap-2 mb-4">
        <LegendCP color="fill-green-500" label="Inspections" />
        <LegendCP color="fill-blue-500" label="Service Calls" />
        <LegendCP color="fill-purple-500" label="Wip Schedules" />
      </div>

      {/* Date and controls */}
      <div className="flex items-center justify-between my-4">
        <button className="text-xl" onClick={handlePreviousDay}>&lt;</button>
        <div className="text-base font-semibold">
          {currentDate.toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'
          })}
        </div>
        <button className="text-xl" onClick={handleNextDay}>&gt;</button>
      </div>
      <select
        className="border rounded px-2 py-1 mb-3 w-full"
        value={selectedDept}
        onChange={e => setSelectedDept(e.target.value)}
      >
        <option value="All">All Departments</option>
        {departments.map(dept => (
          <option key={dept} value={dept}>{dept}</option>
        ))}
      </select>

      {/* Mobile: vertical stack of tech cards */}
      {filteredTechs.map(tech => {
        // Find all events where this tech is lead or helper
        const eventsForTech = events.filter(ev => {
          const isLead = String(ev.tech_id) === String(tech.id);
          const isHelper = Array.isArray(ev.add_tech) && ev.add_tech.some(
            t => String(t.tech_id) === String(tech.id)
          );
          return isLead || isHelper;
        });
        if (!eventsForTech.length) return null;
        return (
          <div key={tech.id} className="mb-4 bg-white rounded-lg shadow">
            <div className="bg-gray-100 rounded-t-lg px-4 py-2 flex items-center gap-2">
              <span className="font-bold text-base">{tech.first_name} {tech.last_name}</span>
              <span className="text-xs text-gray-500">{tech.department}</span>
            </div>
            <div className="divide-y">
              {eventsForTech.map(ev => (
                <div
                  key={ev.id + ev.type}
                  className={`p-2 flex flex-col gap-1
                    ${ev.type === 'inspection' ? 'bg-green-100' : ''}
                    ${ev.type === 'service' ? 'bg-blue-100' : ''}
                    ${ev.type === 'wip' ? 'bg-purple-100' : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="font-semibold truncate">{ev.job_site}</div>
                    <div className="text-xs text-gray-700 font-bold capitalize">
                      {ev.type}
                      {Array.isArray(ev.add_tech) && ev.add_tech.some(t => String(t.tech_id) === String(tech.id)) &&
                        <span className="ml-1 text-purple-600 font-semibold">Helper</span>
                      }
                    </div>
                  </div>
                  <div className="truncate text-gray-700">{ev.customer}</div>
                  <div className="text-xs text-gray-600">
                    {formatTimeTo12HourUtil(ev.start_time)} - {formatTimeTo12HourUtil(ev.end_time)}
                  </div>
                  {ev.TROUBLE && <div className="text-xs text-gray-500 italic">{ev.TROUBLE}</div>}
                  {ev.STATUS && <div className="text-xs text-gray-600">Status: {ev.STATUS}</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Fallback if no events */}
      {!filteredTechs.some(tech =>
        events.some(ev =>
          String(ev.tech_id) === String(tech.id) ||
          (Array.isArray(ev.add_tech) && ev.add_tech.some(t => String(t.tech_id) === String(tech.id)))
        )
      ) && (
        <div className="mt-10 text-center text-gray-400">No events scheduled for this day.</div>
      )}
    </div>
  );
};

export default FullViewerMobile;
